import { GetGxPSettings, SaveGxPSettings } from '@/apis/settings';
import type {
  GxPAutoReleaseRequirement,
  GxPEvaluationSettingsData,
  GxPEvaluationSettingsPayload,
} from './types';
import { useToken } from '@/common/helper';
import {
  GxPEndEvent,
  GxPSettingsOffset,
  GxPStartEvent,
} from '@/common/static_data';
import {
  CustomButton,
  CustomInput,
  CustomSelect,
  CustomShipmentText,
} from '@/components';
import { useSpin } from '@/components/SpinContext';
import useUnauthorize from '@/hooks/useUnauthorize';
import { DownOutlined } from '@ant-design/icons';
import { Checkbox, Col, Divider, Flex, Form, message, Row } from 'antd';
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

interface EvaluationFormValues {
  start_event: string;
  end_event: string;
  start_offset: string;
  end_offset: string;
  post_pod_hold?: string;
  auto_release_requirements: string[];
}

interface EvaluationFormProps {
  onClose?: () => void;
}

const GXP_WINDOW_EVENT_BASIS = 'signature';

const getStringValue = (value: string | number | null | undefined) =>
  value !== null && value !== undefined ? String(value) : undefined;

const resolveSettingsData = (
  responseData:
    | GxPEvaluationSettingsData
    | {
        is_success?: boolean;
        message?: string;
        data?: GxPEvaluationSettingsData;
      }
    | undefined
) => {
  if (!responseData) return undefined;
  if (
    'start_event_basis' in responseData ||
    'window_basis_and_holds' in responseData
  )
    return responseData;
  return 'data' in responseData ? responseData.data : undefined;
};

const getSettingsWindow = (data: GxPEvaluationSettingsData) => {
  if ('window_basis_and_holds' in data && data.window_basis_and_holds) {
    return data.window_basis_and_holds;
  }

  return data as GxPEvaluationSettingsPayload;
};

const getSettingsRequirements = (
  data: GxPEvaluationSettingsData
): GxPAutoReleaseRequirement[] => {
  if ('disposition' in data && data.disposition?.auto_release_requirements) {
    return data.disposition.auto_release_requirements;
  }

  if ('auto_release_requirements' in data) {
    return Object.entries(data.auto_release_requirements || {}).map(
      ([key, enabled]) => ({
        key,
        label: key
          .split('_')
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' '),
        mandatory: false,
        enabled,
        locked: false,
      })
    );
  }

  return [];
};

const getProductSettingsNote = (data?: GxPEvaluationSettingsData) =>
  data && 'product_settings_note' in data && data.product_settings_note
    ? data.product_settings_note
    : 'Limits, MKT, activation energy, and time-out-of-range bands live in Product settings.';

const getProductSettingsPath = (data?: GxPEvaluationSettingsData) =>
  data &&
  'product_settings_path' in data &&
  data.product_settings_path &&
  data.product_settings_path !== '/settings/products'
    ? data.product_settings_path
    : '/settings/product';

const getDispositionHelpText = (data?: GxPEvaluationSettingsData) =>
  data && 'disposition' in data && data.disposition?.help_text
    ? data.disposition.help_text
    : 'Any report that does not meet every requirement above is held for review';

const SettingsSelectField = ({
  label,
  name,
  options,
}: {
  label: string;
  name: string;
  options: { label: string; value: string; disabled?: boolean }[];
}) => (
  <Col span={12}>
    <Form.Item
      className='custom-input form-input-style mb-0'
      label={label}
      name={name}
      rules={[{ required: true, message: 'Please select value' }]}
    >
      <CustomSelect
        suffixIcon={<DownOutlined />}
        placeholder='Select'
        height='36px'
        border='0px'
        bgColor='var(--light-gray)'
        textColor='var(--text-secondary)'
        borderRadius='8px'
        placeholderColor='var(--text-secondary)'
        lineHeight='38px'
        options={options}
      />
    </Form.Item>
  </Col>
);

const EvaluationForm: React.FC<EvaluationFormProps> = ({ onClose }) => {
  const [form] = Form.useForm<EvaluationFormValues>();
  const { token } = useToken();
  const { unauthorize } = useUnauthorize();
  const { showSpin, hideSpin } = useSpin();
  const [isSaving, setIsSaving] = useState(false);
  const [settingsData, setSettingsData] = useState<GxPEvaluationSettingsData>();
  const [autoReleaseOptions, setAutoReleaseOptions] = useState<
    { label: string; value: string; disabled?: boolean; mandatory?: boolean }[]
  >([]);

  const gxpReportEvents =
    Form.useWatch('auto_release_requirements', form) || [];

  useEffect(() => {
    showSpin();
    GetGxPSettings(token)
      .then((res) => {
        const responseData = res?.data?.data;
        const data = resolveSettingsData(responseData);
        if (data) {
          const windowSettings = getSettingsWindow(data);
          const requirements = getSettingsRequirements(data);
          setSettingsData(data);
          setAutoReleaseOptions(
            requirements.map((item) => ({
              label: item.label,
              value: item.key.toUpperCase(),
              disabled: item.mandatory,
              mandatory: item.mandatory,
            }))
          );
          form.setFieldsValue({
            start_event: GXP_WINDOW_EVENT_BASIS,
            end_event: GXP_WINDOW_EVENT_BASIS,
            start_offset:
              getStringValue(windowSettings.start_offset_minutes) ?? '0',
            end_offset:
              getStringValue(windowSettings.end_offset_minutes) ?? '0',
            post_pod_hold:
              windowSettings.post_pod_hold_minutes !== null &&
              windowSettings.post_pod_hold_minutes !== undefined
                ? String(windowSettings.post_pod_hold_minutes)
                : '120',
            auto_release_requirements: requirements
              .filter((item) => item.enabled)
              .map((item) => item.key.toUpperCase()),
          });
        }
      })
      .catch((error) => {
        unauthorize(error);
      })
      .finally(() => {
        hideSpin();
      });
  }, [token, form, showSpin, hideSpin, unauthorize]);

  const onFinish = (values: EvaluationFormValues) => {
    const selectedRequirements = new Set(values.auto_release_requirements);

    const model: GxPEvaluationSettingsPayload = {
      start_event_basis: GXP_WINDOW_EVENT_BASIS,
      end_event_basis: GXP_WINDOW_EVENT_BASIS,
      start_offset_minutes: Number(values.start_offset),
      end_offset_minutes: Number(values.end_offset),
      post_pod_hold_minutes: values.post_pod_hold
        ? Number(values.post_pod_hold)
        : 120,
      auto_release_requirements: Object.fromEntries(
        autoReleaseOptions.map((option) => [
          option.value.toLowerCase(),
          option.mandatory || selectedRequirements.has(option.value),
        ])
      ),
    };

    setIsSaving(true);
    showSpin();
    SaveGxPSettings(token, model)
      .then((res) => {
        const data = res?.data?.data;
        if (data?.is_success) {
          message.success(data?.message || 'GxP settings saved successfully');
          onClose?.();
        } else {
          message.error(data?.message || 'Failed to save GxP settings');
        }
      })
      .catch((error) => {
        unauthorize(error);
      })
      .finally(() => {
        hideSpin();
        setIsSaving(false);
      });
  };

  return (
    <Form
      form={form}
      layout='vertical'
      onFinish={onFinish}
      initialValues={{
        start_event: GXP_WINDOW_EVENT_BASIS,
        end_event: GXP_WINDOW_EVENT_BASIS,
        post_pod_hold: '120',
      }}
      className='h-full'
    >
      <Row gutter={[8, 24]}>
        <Col span={24}>
          <p className='gxp-link-text'>
            {getProductSettingsNote(settingsData).replace(
              'Product settings.',
              ''
            )}
            <span>
              {' '}
              <Link to={getProductSettingsPath(settingsData)}>
                Product settings
              </Link>
            </span>
            .
          </p>
        </Col>
        <Col span={24}>
          <CustomShipmentText
            text={String('Window basis & holds')}
            fontSize='16px'
            fontWeight={600}
          />
        </Col>

        <SettingsSelectField
          label='Start event'
          name='start_event'
          options={GxPStartEvent}
        />

        <SettingsSelectField
          label='End event'
          name='end_event'
          options={GxPEndEvent}
        />

        <SettingsSelectField
          label='Start offset'
          name='start_offset'
          options={GxPSettingsOffset}
        />

        <SettingsSelectField
          label='End offset'
          name='end_offset'
          options={GxPSettingsOffset}
        />

        <Col span={24}>
          <Form.Item
            className='custom-input form-input-style mb-0'
            label='Post-POD hold for missing data'
            name='post_pod_hold'
            rules={[
              {
                required: true,
                pattern: /^\d{1,5}$/,
                message: 'Enter up to 5 digits only!',
              },
            ]}
          >
            <CustomInput
              placeholder='Enter value'
              height='36px'
              border='0px'
              bgColor='var(--light-gray)'
              textColor='var(--text-secondary)'
              borderRadius='8px'
              placeholderColor='var(--text-secondary)'
              lineHeight='38px'
              suffixFontWeight='600'
              suffixColor='#000'
              suffix='min'
            />
          </Form.Item>
          <CustomShipmentText
            text={String(
              'Wait after POD for late telemetry before finalizing a report'
            )}
            color='var(--text-secondary)'
            marginTop={'4px'}
          />
        </Col>
        <Col span={24}>
          <Divider className='m-0' />
        </Col>
        <Col span={24}>
          <CustomShipmentText
            text={String('Disposition')}
            fontSize='16px'
            fontWeight={600}
          />
        </Col>

        <Col span={24}>
          <Form.Item
            className='custom-input form-input-style'
            name='auto_release_requirements'
            label='Auto-release requirements'
            rules={[
              {
                required: true,
                message: 'Please select a report event',
              },
            ]}
          >
            <CustomSelect
              className='gxp-report-event gray-tag-select'
              mode='multiple'
              width='100%'
              placeholder='Select...'
              suffixIcon={<DownOutlined />}
              options={autoReleaseOptions}
              bgColor='var(--light-gray)'
              textColor='var(--text-secondary)'
              borderRadius='8px'
              height='auto'
              border='none'
              allowClear
              placeholderColor='var(--text-secondary)'
              filterOption={false}
              showSearch={false}
              menuItemSelectedIcon={null}
              optionRender={(option) => (
                <Flex align='center' gap={8}>
                  <Checkbox
                    checked={gxpReportEvents.includes(option.value as string)}
                    style={{ transform: 'scale(0.75)' }}
                  />
                  <span className='font-12'>{option.label}</span>
                </Flex>
              )}
            />
          </Form.Item>
          <CustomShipmentText
            text={String(getDispositionHelpText(settingsData))}
            color='var(--text-secondary)'
            marginTop={'4px'}
          />
        </Col>
      </Row>

      <div className='bottom-action-buttons'>
        <Row gutter={[0, 8]} className='mt-0'>
          <CustomButton
            text={'Save'}
            width={'100%'}
            height={48}
            borderRadius={'8px'}
            bgColor='#000'
            color='#fff'
            border='0px'
            fontSize={'16px'}
            fontWeight={600}
            htmlType='submit'
            isFormValid={isSaving}
          />
          <CustomButton
            key='button1'
            width={'100%'}
            text={'Cancel'}
            bgColor='transparent'
            color='black'
            fontSize='16px'
            fontWeight='600'
            boxShadow='none'
            height='48px'
            onClick={() => onClose?.()}
          />
        </Row>
      </div>
    </Form>
  );
};

export default EvaluationForm;
